import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { fieldErrorProps, fieldProps } from '@/lib/field-props';
import { AlertCircle } from 'lucide-react';

interface ContactFormFieldProps {
    name: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
    type?: 'text' | 'email';
    maxLength?: number;
    placeholder?: string;
    /** Renders a textarea with this many rows instead of a single-line input. */
    rows?: number;
}

/**
 * Label, input and inline error for the public contact forms
 * (home page contact section and /verbaende partner inquiry).
 */
export default function ContactFormField({
    name,
    label,
    value,
    onChange,
    error,
    disabled,
    type = 'text',
    maxLength,
    placeholder,
    rows,
}: ContactFormFieldProps) {
    const sharedProps = {
        id: name,
        name,
        maxLength,
        placeholder,
        required: true,
        disabled,
        value,
        ...fieldProps(name, error),
        className: error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '',
    };

    return (
        <div className="space-y-2">
            <label htmlFor={name} className="text-sm font-medium text-headline">
                {label}
            </label>
            {rows ? (
                <Textarea {...sharedProps} rows={rows} onChange={(event) => onChange(event.target.value)} />
            ) : (
                <Input {...sharedProps} type={type} onChange={(event) => onChange(event.target.value)} />
            )}
            {error && (
                <p {...fieldErrorProps(name)} className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                </p>
            )}
        </div>
    );
}
